/**
 * useFirefliesIntegration — Fireflies.ai API and webhook integration.
 *
 * Users provide a Fireflies API key. The backend polls for new transcripts
 * and can also receive Fireflies webhooks for near-real-time ingestion.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { bulkAssignNotes } from '@/lib/bulkNoteActions';

export interface FirefliesConnectionStatus {
  configured: boolean;
  enabled?: boolean;
  totalIngested?: number;
  lastSyncedAt?: Date | null;
  lastError?: string | null;
  recentCount?: number;
  unassignedCount?: number;
  webhookUrl?: string;
}

export interface UnassignedFirefliesNote {
  id: string;
  name: string;
  meetingDate: Date;
  attendees: string[];
  classificationConfidence: number;
  classificationReasoning: string;
  metadata: {
    firefliesSummary?: string;
    firefliesActionItems?: string[];
    firefliesTopics?: string[];
  };
  createdAt: Date;
}

export interface FirefliesSyncResult {
  success: boolean;
  transcriptsFound: number;
  transcriptsIngested: number;
  transcriptsSkipped: number;
  errors: string[];
  durationMs: number;
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && value !== null && '_seconds' in value) {
    return new Date((value as { _seconds: number })._seconds * 1000);
  }
  if (typeof (value as { toDate?: unknown })?.toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date(value as string | number);
}

export function useFirefliesIntegration() {
  const { user, userProfile } = useAuth();
  const [status, setStatus] = useState<FirefliesConnectionStatus>({ configured: false });
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [unassignedNotes, setUnassignedNotes] = useState<UnassignedFirefliesNote[]>([]);

  useEffect(() => {
    if (!user?.uid) {
      setStatus({ configured: false });
      setLoadingStatus(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const fn = httpsCallable(functions, 'getFirefliesConnectionStatus');
        const result = await fn({});
        const data = result.data as FirefliesConnectionStatus & { lastSyncedAt?: unknown };
        setStatus({
          ...data,
          lastSyncedAt: toDate(data.lastSyncedAt),
        });
      } catch (error) {
        console.error('[useFirefliesIntegration] Failed to fetch status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchStatus();
  }, [user?.uid]);

  const orgId = userProfile?.organizationId;
  useEffect(() => {
    if (!user?.uid || !orgId) {
      setUnassignedNotes([]);
      return;
    }

    const q = query(
      collection(db, 'documents'),
      where('transcriptSource', '==', 'fireflies'),
      where('organizationId', '==', orgId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const notes: UnassignedFirefliesNote[] = snap.docs
          .filter((d) => d.data().projectId == null)
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || 'Untitled Meeting',
              meetingDate: data.meetingDate?.toDate?.() || new Date(),
              attendees: data.attendees || [],
              classificationConfidence: data.classificationConfidence || 0,
              classificationReasoning: data.classificationReasoning || '',
              metadata: {
                firefliesSummary: data.metadata?.firefliesSummary,
                firefliesActionItems: data.metadata?.firefliesActionItems,
                firefliesTopics: data.metadata?.firefliesTopics,
              },
              createdAt: data.createdAt?.toDate?.() || new Date(),
            };
          })
          .sort((a, b) => b.meetingDate.getTime() - a.meetingDate.getTime());

        setUnassignedNotes(notes);
      },
      (error) => {
        console.error('[useFirefliesIntegration] Unassigned listener error:', error);
      }
    );

    return () => unsub();
  }, [user?.uid, orgId]);

  const connect = useCallback(async (apiKey: string) => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'configureFirefliesConnection');
      await fn({ apiKey });
      setStatus((prev) => ({
        ...prev,
        configured: true,
        enabled: true,
        lastError: null,
      }));
      toast.success('Fireflies connected. Sync will start within 15 minutes.');
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to connect Fireflies';
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'disconnectFireflies');
      await fn({});
      setStatus({ configured: false });
      toast.success('Fireflies disconnected');
      return true;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to disconnect Fireflies';
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const syncNow = useCallback(async (): Promise<FirefliesSyncResult | null> => {
    setSyncing(true);
    try {
      const fn = httpsCallable<Record<string, never>, FirefliesSyncResult>(functions, 'syncFirefliesNow', {
        timeout: 300_000,
      });
      const result = await fn({});
      const data = result.data;

      if (data.transcriptsIngested > 0) {
        toast.success(`Synced ${data.transcriptsIngested} Fireflies transcript${data.transcriptsIngested > 1 ? 's' : ''}`);
      } else if (data.transcriptsFound === 0) {
        toast.info('No new Fireflies transcripts found');
      } else {
        toast.info(`${data.transcriptsSkipped} transcript(s) already synced`);
      }

      setStatus((prev) => ({
        ...prev,
        lastSyncedAt: new Date(),
        totalIngested: (prev.totalIngested || 0) + data.transcriptsIngested,
      }));

      return data;
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Fireflies sync failed';
      toast.error(msg);
      return null;
    } finally {
      setSyncing(false);
    }
  }, []);

  const assignToProject = useCallback(async (documentId: string, projectId: string) => {
    await bulkAssignNotes([documentId], projectId);
  }, []);

  return {
    status,
    loadingStatus,
    saving,
    syncing,
    unassignedNotes,
    unassignedCount: unassignedNotes.length,
    connect,
    disconnect,
    syncNow,
    assignToProject,
  };
}
